import {
  isStructuralSection,
  type ReportTemplateSection,
  type ReportTemplateSectionDesignInput,
} from '@/services/report-template.service';
import { sameWidths, widthsOf } from '@/features/inspection/components/report/column-width';

/**
 * Pure design-draft logic for the MSE Report Template Designer (B6.9e).
 *
 * Kept out of the component so the invariants below can be tested directly
 * rather than inferred from rendered output — the same split
 * `column-width.ts` uses for the resizer arithmetic.
 *
 * The governing rule: this module NEVER decides what is allowed. It reports what
 * the API said (`canBeEngineerToggleable`, `isWidthEditable`, `minWidthFraction`)
 * and applies the one business rule the read API does not expose as a field —
 * D-079's locked structural visibility. Everything else is the server's word.
 */

/** A section as the designer holds it while editing. */
export type DraftSection = ReportTemplateSection;

/**
 * Whether the MSE may change this section's visibility.
 *
 * D-079: the four structural sections must remain visible in any publishable
 * version, so the designer never offers a switch that the publish gate would
 * reject. Content sections are freely hideable — hiding one is an ordinary
 * design decision, and B6.9d proved such a version still publishes.
 */
export function canEditVisibility(section: DraftSection): boolean {
  return !isStructuralSection(section.sectionKey);
}

/**
 * Whether the MSE may open this section to engineer toggling.
 *
 * Read straight from the API's `canBeEngineerToggleable` — never re-derived from
 * the section key. The server enforces the same allow-list at write time and,
 * independently, at publish.
 */
export function canEditEngineerToggleable(section: DraftSection): boolean {
  return section.canBeEngineerToggleable;
}

/**
 * A contradiction between this module's structural rule and the API's own
 * metadata, or between the API's metadata and D-079.
 *
 * Returns a human-readable reason, or null when the document is coherent.
 *
 * Brief §9 requires a safe error state rather than silently rewriting server
 * data if a structural section arrives hidden — a template in that state cannot
 * be published, and quietly flipping the switch back would hide a real data
 * problem behind a UI that looked fine. §30's converse is covered too: if the
 * API ever reported a structural section as engineer-toggleable, that is
 * surfaced rather than trusted or overridden.
 */
export function structuralMismatch(sections: readonly DraftSection[]): string | null {
  for (const s of sections) {
    if (!isStructuralSection(s.sectionKey)) continue;
    if (!s.isVisible) {
      return `${s.sectionKey} is a mandatory structural section but the server reports it as hidden. This template cannot be published until that is corrected.`;
    }
    if (s.canBeEngineerToggleable) {
      return `${s.sectionKey} is a mandatory structural section but the server reports it as engineer-toggleable. Refusing to edit a design that contradicts itself.`;
    }
  }
  return null;
}

/** The label to show for a section: override, else the server's default, else the key. */
export function sectionLabel(section: DraftSection): string {
  return section.title ?? section.defaultTitle ?? section.sectionKey;
}

/**
 * Are two designs the same?
 *
 * Compares the six designable properties plus column widths, in displayed
 * order. `position` is deliberately NOT compared as a number — it is derived
 * from array index at save time, so comparing stored positions would flag a
 * template as dirty merely because the server renumbered. That is the same
 * false-positive `sameComposition` had to fix on the SE side.
 *
 * Widths are compared EXACTLY. The backend stores `DECIMAL(18,17)` and writes a
 * column only when the stored bytes would differ, so tolerating "close enough"
 * would report an untouched template as edited, and rounding would hide a real
 * edit.
 */
export function sameDesign(a: readonly DraftSection[], b: readonly DraftSection[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((s, i) => {
    const other = b[i]!;
    return (
      s.sectionKey === other.sectionKey &&
      s.title === other.title &&
      s.isVisible === other.isVisible &&
      s.hideWhenEmpty === other.hideWhenEmpty &&
      s.isEngineerToggleable === other.isEngineerToggleable &&
      sameWidths(widthsOf(s.columns), widthsOf(other.columns))
    );
  });
}

/**
 * Builds the complete B6.9c design payload from the displayed order.
 *
 * Three properties are load-bearing:
 *
 *  - **Complete, never sparse.** Every section appears exactly once; the API
 *    rejects a missing or duplicated key.
 *  - **Positions come from the displayed order**, so a reorder is unambiguous.
 *  - **`columnWidths` only where the API said the widths are editable.** Sending
 *    them for a full-width block is a 400, not a silent no-op — so the UI simply
 *    never builds a payload the server would turn down.
 *
 * Widths are passed through as the exact doubles the resizer produced. No
 * `toFixed`, no rounding, no renormalisation: the percentages shown to humans
 * are a display concern and never touch what is sent.
 */
export function toDesignPayload(sections: readonly DraftSection[]): {
  sections: ReportTemplateSectionDesignInput[];
} {
  return {
    sections: sections.map((s, index) => ({
      sectionKey: s.sectionKey,
      position: index,
      isVisible: s.isVisible,
      hideWhenEmpty: s.hideWhenEmpty,
      isEngineerToggleable: s.isEngineerToggleable,
      // null and absent mean the same thing to the API (restore the built-in
      // title); sending null explicitly makes "cleared" unambiguous.
      title: s.title,
      ...(s.isWidthEditable ? { columnWidths: widthsOf(s.columns) } : {}),
    })),
  };
}

/**
 * The per-column minimum for a section's width editor.
 *
 * The detail API reports `minWidthFraction` once per SECTION — it is the same
 * renderer-derived bound for every column in it — while the shared resizer works
 * in terms of per-column minimums. This projection is the whole adaptation, and
 * it consumes the server's number rather than recomputing anything.
 */
export function columnsWithMinimum(section: DraftSection) {
  return section.columns.map((c) => ({ ...c, minWidthFraction: section.minWidthFraction }));
}
